// Flow 6 - Production Build: the hardening checklist, the environment ladder, and the
// default-deny promote confirm (docs/phase-0/slice5-ux-flows.md §9; route
// `/apps/:appId/build/production`). Requires `app.stage === "mvp"` - reuses
// `LifecycleGateCard.tsx` verbatim for the stage gate (this closes its own
// `onGoToBuild` stub for good, ux spec §2.1).
//
// ADR-0022 (Slice 5 close): the Production Build path itself - the production-readiness
// read (`GET /build/production`), the checklist patch, and the promote flow that depends
// on it - is DEFERRED to Slice 18, coupled to real build execution (no hardening-evidence
// persistence exists yet, so `canPromoteProduction` is always false and promote is always
// fail-closed, CR-033). Past the stage gate below, this screen no longer fetches
// readiness or renders a live checklist/ladder/promote flow; it renders an explicit,
// honest "coming with real build execution" preview instead (ADR-0022 decision 2) - no
// call to a route that does not exist, no button that 404s.
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import { AppId } from "@nightcrew/shared";
import type { AppView } from "@nightcrew/shared";
import { FocusedFlowShell } from "../../components/FocusedFlowShell.js";
import { LifecycleGateCard } from "../../components/LifecycleGateCard.js";
import { getApp } from "../../api/apps.js";
import { buildCopy } from "../../copy/build.js";
import { canEnterProductionBuild } from "./gate.js";

type ScreenState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | { readonly kind: "ready"; readonly app: AppView };

export const ProductionBuild = (): React.JSX.Element => {
  const params = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const parsed = params.appId !== undefined ? AppId.safeParse(params.appId) : null;
  const appId = parsed?.success === true ? parsed.data : null;

  const [state, setState] = useState<ScreenState>({ kind: "loading" });

  useEffect(() => {
    if (appId === null) return;
    const cancelled = { current: false };
    setState({ kind: "loading" });
    void (async () => {
      const appResult = await getApp(appId);
      if (cancelled.current) return;
      setState(isErr(appResult) ? { kind: "error" } : { kind: "ready", app: appResult.value });
    })();
    return () => {
      cancelled.current = true;
    };
  }, [appId]);

  if (appId === null) {
    return (
      <FocusedFlowShell title={buildCopy("build.production.titleGeneric")}>
        <div className="card state-card">
          <p>We can&apos;t find that app.</p>
        </div>
      </FocusedFlowShell>
    );
  }

  if (state.kind === "loading") {
    return (
      <FocusedFlowShell title={buildCopy("build.production.titleGeneric")}>
        <div className="stack gap12">
          <div className="card pad skeleton-card" />
          <div className="card pad skeleton-card" />
        </div>
      </FocusedFlowShell>
    );
  }

  if (state.kind === "error") {
    return (
      <FocusedFlowShell title={buildCopy("build.production.titleGeneric")}>
        <div className="card state-card">
          <p>{buildCopy("build.production.error")}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setState({ kind: "loading" });
            }}
          >
            {buildCopy("build.errors.generic.retryCta")}
          </button>
        </div>
      </FocusedFlowShell>
    );
  }

  const { app } = state;

  if (!canEnterProductionBuild(app.stage)) {
    return (
      <FocusedFlowShell title={buildCopy("build.production.titleGeneric")}>
        <LifecycleGateCard
          appName={app.name}
          heading={buildCopy("build.production.stageGate.heading", { appName: app.name })}
          body={buildCopy("build.production.stageGate.body")}
          buildLinkCta={buildCopy("build.production.stageGate.buildLinkCta")}
          onGoToBuild={() => {
            void navigate(`/apps/${appId}/build`);
          }}
        />
      </FocusedFlowShell>
    );
  }

  // ---- Past the stage gate: the honest deferred-state preview (ADR-0022) ----
  return (
    <FocusedFlowShell
      title={buildCopy("build.production.title", { appName: app.name })}
      onBack={() => {
        void navigate(`/apps/${appId}/build`);
      }}
    >
      <h1 className="h2">{buildCopy("build.production.title", { appName: app.name })}</h1>

      <div
        className="card state-card state-lifecycle mt16"
        role="region"
        aria-label={buildCopy("build.production.deferred.heading")}
        data-testid="production-deferred-panel"
      >
        <h2 className="h3">{buildCopy("build.production.deferred.heading")}</h2>
        <p className="muted">{buildCopy("build.production.deferred.body", { appName: app.name })}</p>

        <section className="deferred-preview-section" aria-labelledby="hardening-preview-heading">
          <h3 id="hardening-preview-heading">{buildCopy("build.production.checklist.heading")}</h3>
          <p className="muted mt8" style={{ fontSize: 13.5 }}>
            {buildCopy("build.production.deferred.checklistPreview", { appName: app.name })}
          </p>
        </section>

        <section className="deferred-preview-section" aria-labelledby="ladder-preview-heading">
          <h3 id="ladder-preview-heading">{buildCopy("build.production.ladder.heading")}</h3>
          <p className="muted mt8" style={{ fontSize: 13.5 }}>
            {buildCopy("build.production.deferred.ladderPreview", { appName: app.name })}
          </p>
        </section>
      </div>
    </FocusedFlowShell>
  );
};
